import {
    AlgebraQuestionOptions,
    AverageQuestionOptions,
    BarGraphQuestionOptions,
    ConversionQuestionOptions,
    FactFamilyOptions,
    FractionDenominatorOptions,
    FractionQuestionOptions,
    GeometryQuestionOptions,
    IntegerQuestionOptions,
    RationalKindOptions,
    LinePlotMarkOptions,
    HistogramQuestionOptions,
    BoxPlotQuestionOptions,
    LinePlotQuestionOptions,
    LinePlotScaleOptions,
    CoordinateQuestionOptions,
    CoordinateGridOptions,
    CalendarQuestionOptions,
    CalendarSpanOptions,
    CalendarMonthOptions,
    MissingNumberOptions,
    MoneyQuestionOptions,
    NumberLineQuestionOptions,
    NumberPropertyOptions,
    Operations,
    OrderOfOperationsLevelOptions,
    PatternQuestionOptions,
    PercentQuestionOptions,
    PlaceValueOptions,
    ProblemFormatOptions,
    RatioQuestionOptions,
    RegroupingOptions,
    RomanQuestionOptions,
    RoundingComparingOptions,
    RoundingDisplayOptions,
    RoundingPlaceOptions,
    ShapeQuestionOptions,
    StoryTypeOptions,
    TimeDisplayOptions,
    TimeIntervalOptions,
    TimeQuestionOptions,
    VolumeQuestionOptions,
    CircleQuestionOptions,
    ProbabilityQuestionOptions,
    PythagoreanQuestionOptions,
} from './Constants.ts';
import { PrintedLanguage } from './PrintedLanguage.ts';

/**
 * ADR 0051 §3: an enum value in this generator is three things at once - an
 * on-screen label, a URL value (`fq=Simplify`) and a manifest override
 * `routeSeo.json` pins by name. Those three uses cannot be allowed to drift,
 * which is why a value is never translated (ADR 0051 §2) - but the label a
 * person reads has to be free to change independently of the value it is
 * shown for, or "translate this label" would silently mean "rewrite every
 * shared link, route override and reproducible-worksheet seed".
 *
 * This module is that seam. `labelFor` maps a value to the text a person
 * reads for it; every render site calls it (or `withLabels`, for a settings
 * picker) instead of putting the enum value on the page directly. Part 1
 * (this file, as first written) translated nothing; part 3 (ADR 0051 §7) is
 * where it becomes a real lookup.
 *
 * **Keyed off `language`, the worksheet's printed language - not the
 * interface locale.** These labels name the sheet's own content (a fraction
 * question type, a rounding place, an operation), the same worksheet-state
 * reasoning ADR 0051 §7 applies to the PDF chrome and the prompts. `language`
 * defaults to English, which is what every call site that does not pass one
 * - the page title, the SEO description, the "recent worksheets" history -
 * keeps doing on purpose: ADR 0051 §2/§4 keeps that copy English.
 *
 * `LABELS` stays a flat `value -> label` map per language rather than one
 * table per enum. Two different enums can and do share a value (`Mixed` is
 * one, spelled the same way in a dozen of these enums; `Both` is the other,
 * shared by `RegroupingOptions` and `FractionDenominatorOptions`) and share
 * its label too. ADR 0051's part-2 finding is what settles that this is safe
 * for six of the seven shared values and a deliberate compromise for the
 * seventh (`Both`): Spanish takes the generic masculine *ambos*, and French
 * *les deux* is invariable either way.
 */
const ENUMS_WITH_LABELS: Record<string, string>[] = [
    Operations as unknown as Record<string, string>,
    RegroupingOptions,
    MissingNumberOptions,
    StoryTypeOptions,
    ProblemFormatOptions,
    AlgebraQuestionOptions,
    IntegerQuestionOptions,
    RationalKindOptions,
    NumberPropertyOptions,
    ShapeQuestionOptions,
    VolumeQuestionOptions,
    CircleQuestionOptions,
    ProbabilityQuestionOptions,
    PythagoreanQuestionOptions,
    AverageQuestionOptions,
    BarGraphQuestionOptions,
    RatioQuestionOptions,
    GeometryQuestionOptions,
    PercentQuestionOptions,
    NumberLineQuestionOptions,
    PatternQuestionOptions,
    RomanQuestionOptions,
    LinePlotQuestionOptions,
    LinePlotScaleOptions,
    LinePlotMarkOptions,
    HistogramQuestionOptions,
    BoxPlotQuestionOptions,
    CoordinateQuestionOptions,
    CoordinateGridOptions,
    CalendarQuestionOptions,
    CalendarSpanOptions,
    CalendarMonthOptions,
    ConversionQuestionOptions,
    FactFamilyOptions,
    OrderOfOperationsLevelOptions,
    RoundingComparingOptions,
    RoundingPlaceOptions,
    RoundingDisplayOptions,
    TimeQuestionOptions,
    TimeDisplayOptions,
    TimeIntervalOptions,
    PlaceValueOptions,
    FractionDenominatorOptions,
    FractionQuestionOptions,
    MoneyQuestionOptions,
];

/**
 * Every English value translated into Spanish and French. One entry per
 * distinct English string - not per enum - which is what makes a shared value
 * (`Mixed`, `Both`, `Comparing`, …) resolve to one label automatically.
 */
const TRANSLATIONS: Record<string, { es: string; fr: string }> = {
    // Operations
    Addition: { es: 'Suma', fr: 'Addition' },
    Subtraction: { es: 'Resta', fr: 'Soustraction' },
    Multiplication: { es: 'Multiplicación', fr: 'Multiplication' },
    Division: { es: 'División', fr: 'Division' },
    'Long Division': { es: 'División larga', fr: 'Division posée' },

    // Shared across many enums
    Mixed: { es: 'Mixto', fr: 'Mixte' },
    // ADR 0051 part-2 finding: the one shared value where the flat table is a
    // compromise rather than a coincidence. Spanish's generic masculine reads
    // naturally for both owners (denominators, regrouping); French's "les
    // deux" is invariable either way.
    Both: { es: 'Ambos', fr: 'Les deux' },
    Fractions: { es: 'Fracciones', fr: 'Fractions' },
    Integers: { es: 'Números enteros', fr: 'Nombres entiers' },
    'A number line': { es: 'Una recta numérica', fr: 'Une droite numérique' },
    // IntegerQuestionOptions.Compare and RoundingComparingOptions.Comparing
    // share this value; both read as a noun the way their siblings do.
    Comparing: { es: 'Comparación', fr: 'Comparaison' },
    'Adding & Subtracting': { es: 'Sumar y restar', fr: 'Addition et soustraction' },

    // RegroupingOptions
    'Regrouping Only': { es: 'Solo con reagrupación', fr: 'Avec retenue uniquement' },
    'No Regrouping': { es: 'Sin reagrupación', fr: 'Sans retenue' },

    // MissingNumberOptions
    Off: { es: 'Desactivado', fr: 'Désactivé' },
    'First Number': { es: 'Primer número', fr: 'Premier nombre' },
    'Second Number': { es: 'Segundo número', fr: 'Second nombre' },

    // StoryTypeOptions (ADR 0073)
    Simple: { es: 'Sencillas', fr: 'Simples' },
    Varied: { es: 'Variadas', fr: 'Variées' },

    // ProblemFormatOptions
    'Math Facts': { es: 'Operaciones básicas', fr: 'Calcul mental' },
    'Word Problems': { es: 'Problemas escritos', fr: 'Problèmes' },
    Money: { es: 'Dinero', fr: 'Monnaie' },
    Decimals: { es: 'Decimales', fr: 'Nombres décimaux' },
    'Rounding & Comparing': { es: 'Redondeo y comparación', fr: 'Arrondir et comparer' },
    'Place Value': { es: 'Valor posicional', fr: 'Valeur de position' },
    'Telling Time': { es: 'La hora', fr: "L'heure" },
    'Order of Operations': { es: 'Orden de las operaciones', fr: 'Ordre des opérations' },
    'Fact Families': { es: 'Familias de operaciones', fr: "Familles d'opérations" },
    'Spiral Review': { es: 'Repaso en espiral', fr: 'Révision en spirale' },
    Algebra: { es: 'Álgebra', fr: 'Algèbre' },
    'Number Properties': { es: 'Propiedades de los números', fr: 'Propriétés des nombres' },
    'Unit Conversion': { es: 'Conversión de unidades', fr: "Conversion d'unités" },
    'Number Patterns': { es: 'Patrones numéricos', fr: 'Suites numériques' },
    'Number Line': { es: 'Recta numérica', fr: 'Droite numérique' },
    Percent: { es: 'Porcentaje', fr: 'Pourcentage' },
    Ratio: { es: 'Razón', fr: 'Rapports' },
    'Perimeter & Area': { es: 'Perímetro y área', fr: 'Périmètre et aire' },
    Volume: { es: 'Volumen', fr: 'Volume' },
    Circles: { es: 'Círculos', fr: 'Cercles' },
    Probability: { es: 'Probabilidad', fr: 'Probabilités' },
    'Pythagorean Theorem': { es: 'Teorema de Pitágoras', fr: 'Théorème de Pythagore' },
    Averages: { es: 'Promedios', fr: 'Moyennes' },
    'Bar Graphs': { es: 'Gráficas de barras', fr: 'Diagrammes en barres' },
    Geometry: { es: 'Geometría', fr: 'Géométrie' },
    'Color by Number': { es: 'Colorear por número', fr: 'Coloriage magique' },
    'Roman Numerals': { es: 'Números romanos', fr: 'Chiffres romains' },
    'Line Plots': { es: 'Diagramas de puntos', fr: 'Diagrammes à points' },
    'Coordinate Plane': { es: 'Plano cartesiano', fr: 'Repère cartésien' },
    'Riddle Decoder': { es: 'Adivinanza en clave', fr: 'Devinette codée' },
    'Magic Squares': { es: 'Cuadrados mágicos', fr: 'Carrés magiques' },
    Calendar: { es: 'Calendario', fr: 'Calendrier' },
    Histograms: { es: 'Histogramas', fr: 'Histogrammes' },
    'Box Plots': { es: 'Diagramas de caja', fr: 'Boîtes à moustaches' },

    // AlgebraQuestionOptions
    'Solve for the Variable': { es: 'Resolver la variable', fr: "Résoudre l'inconnue" },
    'Two-Step Equations': { es: 'Ecuaciones de dos pasos', fr: 'Équations à deux étapes' },
    'Variables on Both Sides': { es: 'Incógnita en ambos miembros', fr: 'Inconnue dans les deux membres' },
    'Systems of Equations': { es: 'Sistemas de ecuaciones', fr: "Systèmes d'équations" },
    Inequalities: { es: 'Inecuaciones', fr: 'Inéquations' },
    'Evaluate the Expression': { es: 'Evaluar la expresión', fr: "Évaluer l'expression" },
    'Distributive Property': { es: 'Propiedad distributiva', fr: 'Distributivité' },
    'Combining Like Terms': { es: 'Reducir términos semejantes', fr: 'Réduire les termes semblables' },
    Factoring: { es: 'Factorización', fr: 'Factorisation' },
    'Equivalent Expressions': { es: 'Expresiones equivalentes', fr: 'Expressions équivalentes' },
    'Writing Expressions': { es: 'Escribir expresiones', fr: 'Écrire des expressions' },

    // IntegerQuestionOptions
    'Multiplying & Dividing': { es: 'Multiplicar y dividir', fr: 'Multiplication et division' },
    'Absolute Value': { es: 'Valor absoluto', fr: 'Valeur absolue' },
    // ADR 0122. RationalKindOptions' values (Decimals, Fractions, Both) are shared with
    // the format and denominator labels above.
    'Adding & Subtracting Rationals': {
        es: 'Sumar y restar racionales',
        fr: 'Addition et soustraction de rationnels',
    },
    'Multiplying & Dividing Rationals': {
        es: 'Multiplicar y dividir racionales',
        fr: 'Multiplication et division de rationnels',
    },
    'Mixed Rationals': { es: 'Racionales mixtos', fr: 'Rationnels mixtes' },

    // NumberPropertyOptions
    Factors: { es: 'Factores', fr: 'Diviseurs' },
    Multiples: { es: 'Múltiplos', fr: 'Multiples' },
    'Greatest Common Factor': { es: 'Máximo común divisor', fr: 'Plus grand commun diviseur' },
    'Least Common Multiple': { es: 'Mínimo común múltiplo', fr: 'Plus petit commun multiple' },
    'Even & Odd': { es: 'Pares e impares', fr: 'Pairs et impairs' },
    'Squares & Cubes': { es: 'Cuadrados y cubos', fr: 'Carrés et cubes' },
    // ADR 0132
    'Square Roots': { es: 'Raíces cuadradas', fr: 'Racines carrées' },
    'Cube Roots': { es: 'Raíces cúbicas', fr: 'Racines cubiques' },
    'Estimating Square Roots': { es: 'Estimar raíces cuadradas', fr: 'Estimer des racines carrées' },
    // ADR 0136
    'Exponent Rules': { es: 'Leyes de los exponentes', fr: 'Propriétés des puissances' },
    'Prime & Composite': { es: 'Primos y compuestos', fr: 'Nombres premiers et composés' },
    'Factors & Multiples': { es: 'Factores y múltiplos', fr: 'Diviseurs et multiples' },

    // ShapeQuestionOptions
    Perimeter: { es: 'Perímetro', fr: 'Périmètre' },
    Area: { es: 'Área', fr: 'Aire' },
    'Missing Side': { es: 'Lado desconocido', fr: 'Côté manquant' },
    // ADR 0121. The sheet's own prompt, "What is the area?", is shared.
    'Parallelogram Area': { es: 'Área del paralelogramo', fr: 'Aire du parallélogramme' },
    'Trapezoid Area': { es: 'Área del trapecio', fr: 'Aire du trapèze' },
    'Triangle Area': { es: 'Área del triángulo', fr: 'Aire du triangle' },
    'Polygon Area': { es: 'Área de polígonos', fr: 'Aire de polygones' },
    'Scale Drawing': { es: 'Dibujo a escala', fr: "Dessin à l'échelle" },

    // VolumeQuestionOptions (ADR 0095 §3)
    'Count the cubes': { es: 'Contar los cubos', fr: 'Compter les cubes' },
    'Edge lengths': { es: 'Longitudes de las aristas', fr: 'Longueurs des arêtes' },
    'Missing edge': { es: 'Arista desconocida', fr: 'Arête manquante' },
    'Two boxes': { es: 'Dos cajas', fr: 'Deux boîtes' },
    'Surface Area': { es: 'Área total', fr: 'Aire totale' },
    // ADR 0140
    Cylinders: { es: 'Cilindros', fr: 'Cylindres' },
    Cones: { es: 'Conos', fr: 'Cônes' },
    Spheres: { es: 'Esferas', fr: 'Sphères' },
    'Cylinders, cones and spheres': { es: 'Cilindros, conos y esferas', fr: 'Cylindres, cônes et sphères' },

    // CircleQuestionOptions (ADR 0106 §3). Area is ShapeQuestionOptions' above.
    Circumference: { es: 'Circunferencia', fr: 'Circonférence' },
    'Diameter from Circumference': {
        es: 'Diámetro a partir de la circunferencia',
        fr: 'Diamètre à partir de la circonférence',
    },

    // ProbabilityQuestionOptions (ADR 0108 §2). Mixed is shared.
    'One Spinner': { es: 'Una ruleta', fr: 'Une roulette' },
    'Bag of Counters': { es: 'Bolsa de fichas', fr: 'Sac de jetons' },
    'Two Events': { es: 'Dos eventos', fr: 'Deux événements' },

    // PythagoreanQuestionOptions (ADR 0109 §2). Mixed is shared.
    Hypotenuse: { es: 'Hipotenusa', fr: 'Hypoténuse' },
    'Missing Leg': { es: 'Cateto que falta', fr: 'Côté manquant' },
    'Is It a Right Triangle?': { es: '¿Es un triángulo rectángulo?', fr: 'Est-ce un triangle rectangle ?' },

    // AverageQuestionOptions
    Mean: { es: 'Media', fr: 'Moyenne' },
    Median: { es: 'Mediana', fr: 'Médiane' },
    Mode: { es: 'Moda', fr: 'Mode' },
    Range: { es: 'Rango', fr: 'Étendue' },

    // BarGraphQuestionOptions
    'Reading a Bar': { es: 'Leer una barra', fr: 'Lire une barre' },
    'Comparing Two Bars': { es: 'Comparar dos barras', fr: 'Comparer deux barres' },
    'Adding the Bars': { es: 'Sumar las barras', fr: 'Additionner les barres' },
    'Finding the Largest': { es: 'Encontrar la mayor', fr: 'Trouver la plus grande' },

    // RatioQuestionOptions
    Simplifying: { es: 'Simplificar', fr: 'Simplifier' },
    'Equivalent Ratios': { es: 'Razones equivalentes', fr: 'Rapports équivalents' },
    'Unit Rate': { es: 'Tasa unitaria', fr: 'Taux unitaire' },
    Proportions: { es: 'Proporciones', fr: 'Proportions' },
    'Proportional Tables': { es: 'Tablas proporcionales', fr: 'Tableaux de proportionnalité' },
    'Proportional Graphs': { es: 'Gráficas proporcionales', fr: 'Graphiques de proportionnalité' },

    // GeometryQuestionOptions
    'Acute, Right or Obtuse': { es: 'Agudo, recto u obtuso', fr: 'Aigu, droit ou obtus' },
    'Missing Angle': { es: 'Ángulo desconocido', fr: 'Angle manquant' },
    'Complementary & Supplementary': {
        es: 'Complementarios y suplementarios',
        fr: 'Complémentaires et supplémentaires',
    },
    'Classifying Triangles': { es: 'Clasificar triángulos', fr: 'Classer les triangles' },
    'Naming Polygons': { es: 'Nombrar polígonos', fr: 'Nommer les polygones' },

    // PercentQuestionOptions
    Converting: { es: 'Convertir', fr: 'Convertir' },
    'Percent of a Number': { es: 'Porcentaje de un número', fr: "Pourcentage d'un nombre" },
    'Finding the Percent': { es: 'Hallar el porcentaje', fr: 'Trouver le pourcentage' },
    'Percent of Change': { es: 'Porcentaje de cambio', fr: 'Pourcentage de variation' },
    // ADR 0120: money in a sentence. Impuesto/propina, descuento/aumento.
    'Tax and Tip': { es: 'Impuesto y propina', fr: 'Taxe et pourboire' },
    'Discount and Markup': { es: 'Descuento y aumento', fr: 'Réduction et majoration' },
    'Simple Interest': { es: 'Interés simple', fr: 'Intérêt simple' },
    'Percent Word Problems': { es: 'Problemas de porcentaje', fr: 'Problèmes de pourcentage' },

    // NumberLineQuestionOptions
    'Whole Numbers': { es: 'Números naturales', fr: 'Entiers naturels' },

    // PatternQuestionOptions
    'Skip Counting': { es: 'Conteo salteado', fr: 'Comptage de x en x' },
    'Doubling & Tripling': { es: 'Duplicar y triplicar', fr: 'Doubler et tripler' },

    // RomanQuestionOptions
    'Number to Roman': { es: 'De número a romano', fr: 'Du nombre au chiffre romain' },
    'Roman to Number': { es: 'De romano a número', fr: 'Du chiffre romain au nombre' },

    // LinePlotQuestionOptions (ADR 0084 §5). Median and Range are the
    // averages labels; Mixed is shared.
    'How Many at a Value': { es: 'Cuántos en un valor', fr: 'Combien pour une valeur' },
    'How Many More': { es: 'Cuántos más', fr: 'Combien de plus' },
    'How Many in All': { es: 'Cuántos en total', fr: 'Combien en tout' },
    'Most Common Value': { es: 'Valor más frecuente', fr: 'Valeur la plus fréquente' },
    'Median, Mode and Range': { es: 'Mediana, moda y rango', fr: 'Médiane, mode et étendue' },

    // HistogramQuestionOptions (ADR 0099 §2). Mixed is shared.
    'One Interval': { es: 'Un intervalo', fr: 'Un intervalle' },
    'At Least': { es: 'Como mínimo', fr: 'Au moins' },
    Total: { es: 'Total', fr: 'Total' },
    Most: { es: 'Intervalo con más', fr: 'Intervalle le plus fourni' },

    // BoxPlotQuestionOptions (ADR 0099 §3). Median and Range are the
    // averages labels; Mixed is shared.
    Quartiles: { es: 'Cuartiles', fr: 'Quartiles' },
    'Interquartile Range': { es: 'Rango intercuartílico', fr: 'Écart interquartile' },

    // LinePlotScaleOptions (ADR 0084 §4). Whole Numbers is the number line's.
    Halves: { es: 'Medios', fr: 'Demis' },
    Quarters: { es: 'Cuartos', fr: 'Quarts' },
    Eighths: { es: 'Octavos', fr: 'Huitièmes' },

    // CoordinateQuestionOptions and CoordinateGridOptions (ADR 0085)
    'Read a Point': { es: 'Leer un punto', fr: 'Lire un point' },
    'Name a Point': { es: 'Nombrar un punto', fr: 'Nommer un point' },
    Distance: { es: 'Distancia', fr: 'Distance' },
    Quadrant: { es: 'Cuadrante', fr: 'Quadrant' },
    Polygons: { es: 'Polígonos', fr: 'Polygones' },
    Triangles: { es: 'Triángulos', fr: 'Triangles' },
    'Missing Corner': { es: 'Vértice que falta', fr: 'Sommet manquant' },
    'Distance Between Points': { es: 'Distancia entre puntos', fr: 'Distance entre deux points' },
    'Slope from a Graph': { es: 'Pendiente en una gráfica', fr: "Pente d'une droite tracée" },
    'Slope from Two Points': { es: 'Pendiente entre dos puntos', fr: 'Pente entre deux points' },
    'First Quadrant': { es: 'Primer cuadrante', fr: 'Premier quadrant' },
    'Four Quadrants': { es: 'Cuatro cuadrantes', fr: 'Quatre quadrants' },

    // CalendarQuestionOptions, CalendarSpanOptions and CalendarMonthOptions (ADR 0093)
    'Day of the Week': { es: 'Día de la semana', fr: 'Jour de la semaine' },
    'Find the Date': { es: 'Hallar la fecha', fr: 'Trouver la date' },
    'How Many': { es: 'Cuántos', fr: 'Combien' },
    'Days Before and After': { es: 'Días antes y después', fr: 'Jours avant et après' },
    'Reading the Calendar': { es: 'Leer el calendario', fr: 'Lire le calendrier' },
    'One Day': { es: 'Un día', fr: 'Un jour' },
    'Days and Weeks': { es: 'Días y semanas', fr: 'Jours et semaines' },
    'Up to 20 Days': { es: 'Hasta 20 días', fr: "Jusqu'à 20 jours" },
    'Any Month': { es: 'Cualquier mes', fr: "N'importe quel mois" },
    January: { es: 'Enero', fr: 'Janvier' },
    February: { es: 'Febrero', fr: 'Février' },
    March: { es: 'Marzo', fr: 'Mars' },
    April: { es: 'Abril', fr: 'Avril' },
    May: { es: 'Mayo', fr: 'Mai' },
    June: { es: 'Junio', fr: 'Juin' },
    July: { es: 'Julio', fr: 'Juillet' },
    August: { es: 'Agosto', fr: 'Août' },
    September: { es: 'Septiembre', fr: 'Septembre' },
    October: { es: 'Octubre', fr: 'Octobre' },
    November: { es: 'Noviembre', fr: 'Novembre' },
    December: { es: 'Diciembre', fr: 'Décembre' },

    // LinePlotMarkOptions (ADR 0084 §6)
    X: { es: 'X', fr: 'Croix' },
    Dot: { es: 'Punto', fr: 'Point' },

    // ConversionQuestionOptions
    Metric: { es: 'Métrico', fr: 'Métrique' },
    Customary: { es: 'Sistema inglés', fr: 'Système impérial' },
    Time: { es: 'Tiempo', fr: 'Temps' },

    // FactFamilyOptions
    'Write the four facts': { es: 'Escribe las cuatro operaciones', fr: 'Écris les quatre égalités' },
    'Number bond': { es: 'Diagrama de partes y todo', fr: 'Décomposition en parties' },
    'Find the missing part': { es: 'Halla la parte que falta', fr: 'Trouve la partie manquante' },

    // OrderOfOperationsLevelOptions
    'Two operations': { es: 'Dos operaciones', fr: 'Deux opérations' },
    'Three operations': { es: 'Tres operaciones', fr: 'Trois opérations' },
    'With parentheses': { es: 'Con paréntesis', fr: 'Avec parenthèses' },
    'With exponents': { es: 'Con exponentes', fr: 'Avec exposants' },

    // RoundingComparingOptions
    Rounding: { es: 'Redondeo', fr: 'Arrondi' },

    // RoundingPlaceOptions
    'Nearest 10': { es: 'Decena más cercana', fr: 'Dizaine la plus proche' },
    'Nearest 100': { es: 'Centena más cercana', fr: 'Centaine la plus proche' },
    'Nearest 1,000': { es: 'Millar más cercano', fr: 'Millier le plus proche' },
    'Nearest tenth': { es: 'Décima más cercana', fr: 'Dixième le plus proche' },
    'Nearest whole': { es: 'Entero más cercano', fr: 'Entier le plus proche' },

    // RoundingDisplayOptions
    Plain: { es: 'Sencillo', fr: 'Simple' },

    // TimeQuestionOptions
    'Read the Clock': { es: 'Leer el reloj', fr: "Lire l'heure" },
    'Draw the Hands': { es: 'Dibujar las manecillas', fr: 'Dessiner les aiguilles' },
    'How much time passed': { es: 'Cuánto tiempo pasó', fr: "Combien de temps s'est écoulé" },
    'What time does it end': { es: 'A qué hora termina', fr: 'À quelle heure ça se termine' },
    'What time started': { es: 'A qué hora comenzó', fr: 'À quelle heure ça a commencé' },
    'Mixed elapsed': { es: 'Tiempo transcurrido mixto', fr: 'Durée mixte' },
    'A.M. or P.M.': { es: 'a. m. o p. m.', fr: 'Avant ou après midi' },

    // TimeDisplayOptions
    'Two clock faces': { es: 'Dos relojes', fr: 'Deux cadrans' },
    'Two digital times': { es: 'Dos horas digitales', fr: 'Deux heures numériques' },
    'One of each': { es: 'Uno de cada uno', fr: 'Un de chaque' },
    'A sentence': { es: 'Una oración', fr: 'Une phrase' },

    // TimeIntervalOptions
    Hour: { es: 'Hora', fr: 'Heure' },
    'Half Hour': { es: 'Media hora', fr: 'Demi-heure' },
    'Quarter Hour': { es: 'Cuarto de hora', fr: "Quart d'heure" },
    'Five Minutes': { es: 'Cinco minutos', fr: 'Cinq minutes' },
    Minute: { es: 'Minuto', fr: 'Minute' },

    // PlaceValueOptions
    'Expanded Form': { es: 'Forma desarrollada', fr: 'Écriture développée' },
    'Standard Form': { es: 'Forma estándar', fr: 'Écriture chiffrée' },
    'Digit Value': { es: 'Valor del dígito', fr: 'Valeur du chiffre' },
    'Scientific Notation': { es: 'Notación científica', fr: 'Notation scientifique' },
    'Scientific Notation: Multiply & Divide': {
        es: 'Multiplicar y dividir en notación científica',
        fr: 'Multiplier et diviser en notation scientifique',
    },

    // FractionDenominatorOptions
    'Like Denominators': { es: 'Denominadores iguales', fr: 'Dénominateurs identiques' },
    'Unlike Denominators': { es: 'Denominadores distintos', fr: 'Dénominateurs différents' },

    // FractionQuestionOptions
    'Add, subtract, multiply, divide': {
        es: 'Sumar, restar, multiplicar, dividir',
        fr: 'Addition, soustraction, multiplication, division',
    },
    Simplify: { es: 'Simplificar', fr: 'Simplifier' },
    'Equivalent fractions': { es: 'Fracciones equivalentes', fr: 'Fractions équivalentes' },
    Compare: { es: 'Comparar', fr: 'Comparer' },
    'Mixed & improper numbers': {
        es: 'Números mixtos e impropios',
        fr: 'Nombres fractionnaires et impropres',
    },
    'Fraction of a number': { es: 'Fracción de un número', fr: "Fraction d'un nombre" },
    'Fractions to decimals': { es: 'Fracciones a decimales', fr: 'Fractions en décimaux' },
    'Fractions from a picture': { es: 'Fracciones a partir de un dibujo', fr: "Fractions à partir d'un dessin" },
    'Comparing pictures': { es: 'Comparar dibujos', fr: 'Comparer des dessins' },
    'Mixed fraction skills': { es: 'Destrezas mixtas de fracciones', fr: 'Compétences mixtes sur les fractions' },
    'Mixed picture questions': { es: 'Preguntas mixtas con dibujos', fr: 'Questions mixtes avec dessins' },

    // MoneyQuestionOptions
    'Adding & subtracting': { es: 'Sumar y restar', fr: 'Addition et soustraction' },
    'Counting coins': { es: 'Contar monedas', fr: 'Compter la monnaie' },
    'Making change': { es: 'Dar cambio', fr: 'Rendre la monnaie' },
    'Word problems': { es: 'Problemas verbales', fr: 'Problèmes' },
};

const LABELS_EN: Record<string, string> = {};
for (const enumObject of ENUMS_WITH_LABELS) {
    for (const value of Object.values(enumObject)) {
        LABELS_EN[value] = value;
    }
}

/**
 * The text a person reads for a value that is also a URL key, a manifest
 * override and a stored setting. `language` is the worksheet's printed
 * language (ADR 0051 §7) - default English, which is what every call site
 * that has no worksheet language to hand (a page title, the "recent
 * worksheets" history) keeps using. Falls back to the value itself when a
 * value is somehow missing from the table, so a gap renders something rather
 * than `undefined`.
 */
export function labelFor(value: string, language: PrintedLanguage = 'en'): string {
    if (language === 'en') {
        return LABELS_EN[value] ?? value;
    }
    return TRANSLATIONS[value]?.[language] ?? LABELS_EN[value] ?? value;
}

/**
 * Whether `value` has a real Spanish/French translation recorded, as opposed
 * to falling back to its English value. Exported only so the completeness
 * test can tell a deliberate cognate (French "Minute" is spelled the same as
 * English) apart from a genuine gap - `labelFor`'s own output cannot tell the
 * two apart, since both return the same string.
 */
export function isTranslated(value: string): boolean {
    return Object.prototype.hasOwnProperty.call(TRANSLATIONS, value);
}

/**
 * `Object.values(SomeOptions)` (or a hand-picked subset of it, such as
 * `RoundingComparingSettings`'s decimal/whole split) shaped into
 * `{ label, value }` pairs for a settings picker. PrimeReact's `SelectButton`
 * reads `.value` for selection and, when no `optionValue`/`optionLabel` prop
 * overrides it, falls back to `.label` for what is drawn (see its
 * `getOptionValue`/`getOptionLabel`) - which is exactly the split this
 * function produces, with no further props needed at the call site.
 *
 * Passing the raw enum values straight through as `options`, as every one of
 * these pickers did before ADR 0051, is what made the label and the value
 * the same string in the first place; this is the one place that is undone.
 */
export function withLabels<T extends string>(
    values: readonly T[],
    language: PrintedLanguage = 'en'
): { label: string; value: T }[] {
    return values.map((value) => ({ label: labelFor(value, language), value }));
}
